import React from "react";
import { Link } from "react-router-dom";
import { DossierLabel } from "../dossier/DossierLabel";
import { RecordThumb } from "../media/PublishedMedia";

// Deterministic name -> entity slug (matches backend slug convention).
const entitySlug = (name) =>
  (name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

/**
 * Canonical outer-section building blocks for /topic/<slug>.
 *
 * Each section ALWAYS renders its heading. If the underlying data is
 * absent, the section displays a typed "Editorial pass pending."
 * placeholder so the user never lands on a half-finished dossier
 * without context.
 *
 * Data hydration rules:
 *   timeline       : prefer editorial_modules.timeline → fallback to topic.timeline
 *   key_players    : topic.key_players
 *   evidence       : prefer editorial_modules.evidence → fallback to topic.redacted_lines (one per line)
 *   sources        : topic.sources (root) merged with editorial_modules.sources if v1 exists
 *   related        : editorial_modules.related (only available on v1 dossiers)
 *
 * All sections accept a topic + optional editorialModules so they can
 * be rendered with or without the v1 payload.
 */

const Placeholder = ({ note = "Editorial pass pending." }) => (
  <p
    className="text-[12px] font-mono text-[#757065] italic"
    data-placeholder="true"
  >
    {note}
  </p>
);

const SectionShell = ({ id, label, children, testid }) => (
  <section className="mt-12" id={id} data-testid={testid}>
    <DossierLabel as="h2" className="mt-0">{label}</DossierLabel>
    <div className="mt-4">{children}</div>
  </section>
);

// ---------------------------------------------------------------
// KEY PLAYERS
// ---------------------------------------------------------------
export const KeyPlayersSection = ({ topic }) => {
  const players = (topic?.key_players || []).filter(Boolean);
  return (
    <SectionShell label="Key Players" testid="dossier-key-players-section">
      {players.length === 0 ? (
        <Placeholder />
      ) : (
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="dossier-key-players-list">
          {players.map((p, i) => {
            const name = typeof p === "string" ? p : (p.name || "");
            const role = typeof p === "string" ? "" : (p.role || p.title || "");
            return (
              <li key={i} className="border border-[#2D2A26] bg-[#0d0c0a] p-3 flex items-start gap-3">
                <RecordThumb targetId={entitySlug(name)}
                             className="w-11 h-11 object-cover object-top border border-[#3A3630] shrink-0"
                             fallback={null} />
                <div className="min-w-0">
                  <div className="text-[14px] text-[#E4DFD3] font-heading">{name}</div>
                  {role && (
                    <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-[#A39E93] mt-1">{role}</div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </SectionShell>
  );
};

// ---------------------------------------------------------------
// EVIDENCE LIBRARY
//   v2: editorial_modules.evidence is the structured payload with
//   kind + redaction classification chips.
//   (Legacy redacted_lines fallback removed — those lines already
//   render once as Leaked Excerpts; the Standard forbids duplicates.)
// ---------------------------------------------------------------
export const EvidenceLibrarySection = ({ topic, editorialModules }) => {
  const items = (editorialModules?.evidence || []).filter(Boolean);
  return (
    <SectionShell label="Evidence Library" testid="dossier-evidence-library-section">
      {items.length === 0 ? (
        <Placeholder note="Structured evidence pass pending." />
      ) : (
        <ul className="space-y-3" data-testid="dossier-evidence-list">
          {items.map((item, i) => (
            <li
              key={i}
              className="border border-[#2D2A26] bg-[#0d0c0a] p-3"
              data-testid={`dossier-evidence-${i}`}
            >
              <div className="flex flex-wrap items-center gap-2 mb-1.5">
                {item.kind && (
                  <span className="font-mono text-[8.5px] uppercase tracking-[0.2em] px-1.5 py-[2px] text-[#A68F5E]"
                    style={{ border: "1px solid #6B5A34", borderRadius: 2 }}>
                    {String(item.kind).replace(/_/g, " ")}
                  </span>
                )}
                {item.redaction_level && item.redaction_level !== "none" && (
                  <span className="font-mono text-[8.5px] uppercase tracking-[0.2em] px-1.5 py-[2px] text-[#E08B4A]"
                    style={{ border: "1px solid #9C5A22", borderRadius: 2 }}>
                    {item.redaction_level} redaction
                  </span>
                )}
                {item.label && (
                  <span className="font-mono text-[9px] tracking-[0.2em] text-[#454038]">{item.label}</span>
                )}
              </div>
              {item.title && (
                <div className="font-heading text-[15px] text-[#E4DFD3]">{item.title}</div>
              )}
              {item.summary && (
                <p className="text-[13px] text-[#A39E93] leading-relaxed">
                  {item.summary}
                </p>
              )}
              {item.url && (
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-2 text-[10px] font-mono uppercase tracking-[0.25em] text-[#D92D20] hover:underline"
                >
                  Open source ↗
                </a>
              )}
            </li>
          ))}
        </ul>
      )}
    </SectionShell>
  );
};

// ---------------------------------------------------------------
// RELATED DOSSIERS
//   Only v1 has structured cross-links. If none, we render the
//   placeholder so the heading is never silently missing.
// ---------------------------------------------------------------
export const RelatedDossiersSection = ({ editorialModules }) => {
  const items = (editorialModules?.related || []).filter(Boolean);
  return (
    <SectionShell label="Related Dossiers" testid="dossier-related-section">
      {items.length === 0 ? (
        <Placeholder note="Cross-links pending editorial review." />
      ) : (
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="dossier-related-list">
          {items.map((r, i) => {
            const slug = r.slug || r.id;
            const title = r.title || r.label || slug;
            const inner = (
              <div className="border border-[#2D2A26] bg-[#0d0c0a] p-3 hover:border-[#D92D20] transition-colors">
                <div className="text-[14px] text-[#E4DFD3] font-heading">{title}</div>
                {r.note && (
                  <div className="text-[11px] font-mono text-[#A39E93] mt-1">{r.note}</div>
                )}
              </div>
            );
            return (
              <li key={i} data-testid={`dossier-related-${i}`}>
                {slug ? <Link to={`/topic/${slug}`}>{inner}</Link> : inner}
              </li>
            );
          })}
        </ul>
      )}
    </SectionShell>
  );
};

// ---------------------------------------------------------------
// EXECUTIVE SUMMARY  (was inline JSX in TopicDetail.jsx)
// ---------------------------------------------------------------
export const ExecutiveSummarySection = ({ topic }) => (
  <SectionShell label="Executive Summary" testid="dossier-exec-summary-section">
    {topic?.summary ? (
      <p className="text-lg text-[#E4DFD3] leading-relaxed font-heading">{topic.summary}</p>
    ) : (
      <Placeholder />
    )}
  </SectionShell>
);
